
import { v4 as uuidv4 } from 'uuid';
import { PurchaseHistory } from '@/types/user';
import { supabase } from '@/integrations/supabase/client';

/**
 * Fetches purchase history for a user from Supabase
 */
export const fetchPurchaseHistory = async (userId: string): Promise<PurchaseHistory[]> => {
  try {
    // Fetch purchase history records
    const { data: purchaseHistoryData, error: purchaseHistoryError } = await supabase
      .from('purchase_history')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (purchaseHistoryError) {
      console.error('Error fetching purchase history:', purchaseHistoryError);
      return [];
    }

    if (!purchaseHistoryData || !purchaseHistoryData.length) {
      return [];
    }

    // For each purchase, fetch the items
    const purchaseHistory: PurchaseHistory[] = [];
    
    for (const purchase of purchaseHistoryData) {
      // Fetch items for this purchase
      const { data: itemsData, error: itemsError } = await supabase
        .from('purchase_items')
        .select('*')
        .eq('purchase_id', purchase.id);

      if (itemsError) {
        console.error('Error fetching purchase items:', itemsError);
        continue;
      }

      // Map items to required format
      const items = itemsData?.map(item => ({
        id: item.product_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      })) || [];

      // Get metadata from purchase record and safely cast it
      const metadata = typeof purchase.metadata === 'object' && purchase.metadata !== null 
        ? purchase.metadata as Record<string, any>
        : {};
      
      // Add to purchase history
      purchaseHistory.push({
        id: purchase.id,
        userId: purchase.user_id || userId,
        items,
        total: purchase.total,
        date: new Date(purchase.created_at || Date.now()),
        paymentMethod: purchase.payment_method as 'credit-card' | 'apple-pay' | 'google-pay',
        promoDiscount: metadata.promoDiscount || 0,
        creditApplied: metadata.creditApplied || 0,
        shippingCost: metadata.shippingCost || 0,
        subtotal: metadata.subtotal || purchase.total,
        promoEligibleItems: metadata.promoEligibleItems || []
      });
    }

    return purchaseHistory;
  } catch (error) {
    console.error('Error in fetchPurchaseHistory:', error);
    return [];
  }
};

/**
 * Adds a purchase to Supabase
 */
export const addPurchaseToSupabase = async (
  userId: string,
  items: Array<{ id: number; name: string; price: number; quantity: number }>,
  total: number,
  paymentMethod: 'credit-card' | 'apple-pay' | 'google-pay',
  metadata: Record<string, any> = {}
): Promise<boolean> => {
  try {
    // Generate unique purchase ID
    const purchaseId = uuidv4();
    
    // First insert the purchase history record
    const { error: purchaseError } = await supabase
      .from('purchase_history')
      .insert({
        id: purchaseId,
        user_id: userId,
        total,
        payment_method: paymentMethod,
        metadata
      });
    
    if (purchaseError) {
      console.error('Error inserting purchase history:', purchaseError);
      return false;
    }
    
    // Then insert all purchase items
    if (items.length > 0) {
      const purchaseItems = items.map(item => ({
        purchase_id: purchaseId,
        product_id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      }));
      
      const { error: itemsError } = await supabase
        .from('purchase_items')
        .insert(purchaseItems);
      
      if (itemsError) {
        console.error('Error inserting purchase items:', itemsError);
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error in addPurchaseToSupabase:', error);
    return false;
  }
};

/**
 * Migrates local purchase history to Supabase
 */
export const migrateLocalPurchaseHistory = async (
  userId: string, 
  localPurchaseHistory: any[]
): Promise<boolean> => {
  try {
    // Implementation of migration logic
    console.log(`Migrating ${localPurchaseHistory.length} purchase records for user ${userId}`);
    
    // For each purchase in local history, add it to Supabase
    for (const purchase of localPurchaseHistory) {
      if (purchase.userId === userId) {
        await addPurchaseToSupabase(
          userId, 
          purchase.items, 
          purchase.total, 
          purchase.paymentMethod,
          {
            subtotal: purchase.subtotal,
            promoDiscount: purchase.promoDiscount,
            creditApplied: purchase.creditApplied,
            shippingCost: purchase.shippingCost
          }
        );
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error migrating purchase history:', error);
    return false;
  }
};
